import React from 'react';
import MainLayout from 'components/layout/MainLayout';
import BlogArticle from 'components/blog/BlogArticle';
import { ASSET_PREFIX } from 'utils/constants';
import { NextSeo } from 'next-seo';
import RoundedButton from 'components/RoundedButton';
import Separator from 'components/Separator';
import BlogLink from 'components/blog/BlogLink';
import { getCta } from 'utils/common';

const Article = () => {
  const IMAGE_DIRECTORY = `${ASSET_PREFIX}/images/blog/articles/psychology-behind-different-experiences`;

  return (
    <MainLayout additionalClassName="blogPost">
      <NextSeo
        title="Psychology Behind Experiences | Win Epic Prizes | DAYMADE"
        description="Learn how our minds work and why different experiences make us feel good. Enter now for your chance to win your epic mind-boosting adventure."
        openGraph={{
          title: 'Psychology Behind Experiences | Win Epic Prizes | DAYMADE',
          description:
            'Learn how our minds work and why different experiences make us feel good. Enter now for your chance to win your epic mind-boosting adventure.',
          type: 'article',
          url: 'https://www.daymade.co.uk/blog/psychology-behind-different-experiences',
          images: [
            {
              url: `${IMAGE_DIRECTORY}/hero.jpg`,
              alt: 'A man standing in the sunset in Indonesia',
            },
          ],
        }}
        // additionalMetaTags={[{
        //   name: 'keywords',
        //   content: 'TODO',
        // }]}
      />

      <BlogArticle
        imageDirectory={IMAGE_DIRECTORY}
        heroAltText="A man standing in the sunset in Indonesia"
        heroImageYPosition="0%"
      >
        <>
          <h1 style={{ fontSize: '1.6em', lineHeight: '1.4em' }}>
            The Psychology Behind Different Experiences
          </h1>

          <p className="subheading">
            Discover why experiences make us feel good
          </p>

          <div
            className="separator"
            style={{ width: '80%', marginTop: 0 }}
          />

          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'center',
              marginBottom: '2em',
            }}
          >
            by Violet Currey
          </p>

          <p>
            Social get-togethers, trips with friends, solo adventures, and
            romantic getaways with your loved one are all different experiences
            that do one thing: make you feel good! Most importantly, our minds
            love different experiences and that’s why we are so obsessed with
            getting them on our calendars. But what pleasures are we actually
            getting from them? Well, we’ve got the answers so look no further.
            Learn how your mind works in different situations, from a
            psychological perspective. You’ll understand why some of us seek
            thrills, crave relaxation, or simply want to spend every trip with
            our fave people in our lives. So when you’re organising your next
            experience (or winning your{' '}
            <BlogLink internal="/prizes/trips-and-stays">
              epic holiday prize
            </BlogLink>
            ), you’ll know exactly what is making your mind tick and giving you
            all of those feel-good vibes. Buckle your seatbelts for a ride into
            the mind and how it all works.
          </p>

          <h2>Thrill-seeking & High Octane Activities</h2>
          <p>
            Some people love the fast lane in life and are constantly seeking
            adventure. They’re always up for a challenge and face their fears by
            participating in adrenaline-fueled activities. But why can’t some of
            us get enough of bungee jumping, swimming with sharks, or{' '}
            <BlogLink internal="/prizes/details/adventure-hiking-in-cape-verde">
              adventure hiking through high mountains
            </BlogLink>
            ? It’s all got to do with the adrenaline and dopamine that cause us
            to feel excited. We’ve all heard the term ‘fight or flight’. This is
            the effect that jumping out of a plane causes in your brain by
            pumping it with stress hormones. Why the heck would you want that?
            Well, even though your body is freezing with fear, once you’ve
            actually jumped your brain is reacting as if you’re doing something
            dangerous (which you kinda are). Your brain wants you to get out of
            the situation safely. Once you’ve landed from your killer jump or
            hiked up that high mountain (and you're all safe,) you’re left
            excited, pumped with energy, and that feeling of ‘ahhhhhh!’ So doing
            some wild activities will get that blood rushing so you can feel on
            top of the world.
          </p>
          <img
            src={`${IMAGE_DIRECTORY}/secondary1.jpg`}
            alt="A man paragliding across the mountains"
            className="singleBlogImage"
          />

          <h2>Self-care Experiences</h2>

          <p>
            Some of us need to learn to slow down and take some time out of our
            busy lives to give our minds a break. Some of the best experiences
            we can have are actually with ourselves. Find some relaxation in
            your life, and your mind will thank you. Relaxation allows your mind
            to reset and increases your Serotonin levels which ultimately helps
            with reducing stress, anxiety, and depression. By having an
            outdoorsy holiday in nature, a zen retreat, or pampering yourself at
            a spa getaway, your Serotonin will be getting its fix. You’ll be
            able to take on the week with a little calmness, no matter what life
            throws at you. We get pleasure by watching our fave influencers on
            social media, but those notifications give you a short burst of
            Serotonin that quickly goes, so your brain is constantly looking for
            more. So we give it more by scrolling through social media. But what
            your mind needs instead is some actual switch-off time to replenish
            your Serotonin which will make you feel revitalised. Our{' '}
            <BlogLink internal="/prizes/details/wellness-retreat-wales">
              Wales Wellness retreat
            </BlogLink>{' '}
            travel prize will give you the ultimate self-care experience you
            need to give your mind a relaxing trip away from your busy lives.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary2.jpg`}
            alt="A woman practising yoga in a park"
            className="singleBlogImage"
          />

          <h2>Trips with Friends</h2>

          <p>
            There are a ton of ways to help you with your overall mental
            well-being, such as exercise, sleep, and diet. But one of the keys
            to living a fuller and happier life is a sense of community and
            sharing experiences with others. Holidays and trips with friends,
            family, or even new people help us create stronger bonds. Plus, it’s
            always amazing to share experiences with others so you can reminisce
            about them for years to come (who doesn't love a private joke?).
            Ever had it when you’ve had a long and hard day, and then you have
            an epic weekend with your friends or a nice family weekend away that
            makes you feel a lot more grounded? We’re social animals at the end
            of the day, so human interaction is key to feeling happier and
            reducing stress from our daily lives. Taking a trip with our friends
            is a great way to explore the world, try something new and, to be
            honest, just have a laugh. Adventure with your bestie for a city
            break, epic adventure, or getting lost in nature.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary3.jpg`}
            alt="Four People Standing at the top of Grassy Mountain"
            className="singleBlogImage"
          />

          <h2>New Countries & Cultures</h2>

          <p>
            We’re creatures of comfort, routine and familiarity. But what if we
            told you that getting out of your comfort zone and experiencing a
            new country can help with improving your memory, reducing your
            stress and increasing your creativity? If you want to deepen your
            thinking and understanding of the world, enrich yourself by
            exploring beautiful natural wonders, and learning how other cultures
            celebrate, socialise and go about their days. Our minds love
            learning new things, especially new perspectives on the world. By
            travelling out of your comfort zone, you will grow in confidence,
            self-esteem and, most importantly, your love for travel. Ever heard
            of the saying 'travel bug'? Well, it's your mind that's craving
            this. By listening to new music genres, practising your language
            skills, or taking part in unique celebrations in the world, you are
            making your mind very happy. Ultimately, you'll constantly crave to
            learn more about the world around you. So if you want to keep your
            mind happy and travel more, you can do that by winning the{' '}
            <BlogLink internal="/prizes/details/travel-bundle">
              ultimate travel prize
            </BlogLink>{' '}
            with DAYMADE (win, win, win!).
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary4.jpg`}
            alt="Petra, Jordan"
            className="singleBlogImage"
          />

          <h2>Food & Dining</h2>

          <p>
            Food, glorious food! It’s an exciting experience for our tastebuds,
            and pretty much all of our senses, that makes your mind go crazy (in
            a positive way). Every bite of something that you’ve never tried
            before (especially if it tastes unreal) gives you those sweet,
            feel-good chemicals of dopamine and serotonin. The beauty of eating
            is that this experience can be combined with other experiences. So
            eating with others, trying new food in new countries, and even
            taking a cooking class on your own can make sure your mind is
            getting all the good stuff in one experience. Plus, the experience
            of being wined and dined allows us to relax, and it just feels
            amazing to treat ourselves to gourmet dishes. Really fancy treating
            your tastebuds to a new experience? Our{' '}
            <BlogLink internal="/prizes/details/eat-your-way-through-asia">
              food tour of Asia
            </BlogLink>{' '}
            is the holiday prize for you to eat your way through the best
            cuisine in South East Asia over 20 days.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary5.jpg`}
            alt="Street food in Thailand"
            className="singleBlogImage"
          />

          <h2>Romantic Getaways</h2>

          <p>
            Going away with your loved one is the best way to reinforce your
            bond, and sharing experiences together makes you stronger. You both
            get to bring out the best in each other and challenge yourselves to
            new experiences, or just sit back and relax together. In this day
            and age, you’re both rushing off your feet to keep up with your busy
            work, social, and ‘me time’ schedules, and date nights can sometimes
            be pushed back. Going away together for a staycation in the
            countryside or whisking off for a romantic city break in a European
            country allows you to create memories to last a lifetime. Plus, it’s
            always better to have a laugh and share those moments together. The
            mind loves this because it’s part of our nature to feel loved and
            secure, and getting all romantic together definitely ticks all of
            those boxes.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary6.jpg`}
            alt="A couple sat on a mountain looking at the Lion Rock in Sri Lanka"
            className="singleBlogImage"
          />

          <p>
            Now you have a better understanding of how it all works, all you’ve
            got to do now is pick the experience to get whatever you want out of
            it all! You can mix these experiences up, so you’re giving your mind
            all that good stuff, trying something new, and sharing experiences
            together. Our experience and epic holiday prizes have a range of
            mental benefits to get your serotonin, adrenaline, and dopamine
            pumping. With our prizes, you can feel excited or relaxed, your
            taste buds popping as you bond with your fave people in your life.
          </p>

          <Separator style={{ margin: '1em 0 2.5em' }} />

          <p>
            Enter now for your chance to give your mind an experience of a
            lifetime.
          </p>

          <RoundedButton
            href={getCta()?.url}
            style={{ minWidth: '180px', margin: '1em 0 1.5em' }}
          >
            Enter Now
          </RoundedButton>
        </>
      </BlogArticle>
    </MainLayout>
  );
};

export default Article;
